<script>
import {ref, watch} from "vue";
import useSharedSelectData from "@/use/SharedSelectData";
import StrCatalogAutocomplete from "@/views/Training/Catalog/Components/StrCatalogAutocomplete.vue";
import StrTrainingPlanAutocomplete from "@/views/Training/TrainingPlan/Components/StrTrainingPlanAutocomplete.vue";

export default {
    components: {StrTrainingPlanAutocomplete, StrCatalogAutocomplete},
    props: {
        value: Object,
        isPlan: Boolean,
    },
    name: "TrainingRequestFilter",
    setup(props, {emit}) {
        const {workerPhases, trainingPeriods, quarterlies, halfYearlies, months, validationWorkFlowStatus} = useSharedSelectData();
        const panelModel = ref(0);
        const filter = ref({});

        watch(() => filter.value, () => {
            emit('input', filter.value);
        }, {deep: true});

        return {
            panelModel,
            filter,
            workerPhases,
            trainingPeriods,
            quarterlies,
            halfYearlies,
            months,
            validationWorkFlowStatus
        }
    }
}
</script>

<template>
    <div>
        <v-expansion-panels v-model="panelModel" dense focusable>
            <v-expansion-panel>
                <v-expansion-panel-header>
                    <strong>{{ $t('module.training_request') }}</strong>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="mr-n5 ml-n5">
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12" v-if="!isPlan">
                                <str-training-plan-autocomplete
                                    v-model="filter.planId"
                                    not-clearable
                                    :label="$t('module.training_plan')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <str-catalog-autocomplete
                                    v-model="filter.catalogId"
                                    :label="$t('module.training_catalog')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-text-field
                                    v-model="filter.otherDescription"
                                    :label="$t('training.other_catalog_description')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-enum-select
                                    v-model="filter.period"
                                    :items="trainingPeriods"
                                    :label="$t('training.period')"
                                />
                            </v-col>
                            <v-col cols="12" v-if="filter.period === 'MONTHLY'">
                                <w-enum-select
                                    v-model="filter.periodMonth"
                                    :items="months"
                                    :label="$t('common.month')"
                                />
                            </v-col>
                            <v-col cols="12" v-if="filter.period === 'QUARTERLY'">
                                <w-enum-select
                                    v-model="filter.periodQuarterlyOrHalfYearly"
                                    :items="quarterlies"
                                    :label="$t('common.QUARTERLY')"
                                />
                            </v-col>
                            <v-col cols="12" v-if="filter.period === 'HALF_YEARLY'">
                                <w-enum-select
                                    v-model="filter.periodQuarterlyOrHalfYearly"
                                    :items="halfYearlies"
                                    :label="$t('common.HALF_YEARLY')"
                                />
                            </v-col>
                            <v-col cols="12" v-if="filter.period === 'SPECIFIC'">
                                <w-date-picker
                                    v-model="filter.periodStart"
                                    :label="$t('common.from')"
                                />
                            </v-col>
                            <v-col cols="12" v-if="filter.period === 'SPECIFIC'">
                                <w-date-picker
                                    v-model="filter.periodEnd"
                                    :label="$t('common.to')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <str-worker-autocomplete
                                    v-model="filter.workerIds"
                                    multiple
                                    :label="$t('module.workers')"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
                <v-expansion-panel-header>
                    <strong>{{ $t('module.worker') }}</strong>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="mr-n5 ml-n5">
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12">
                                <organizational-chart-select
                                        v-model="filter.organizationalchartIds"
                                        :label="$t('module.organizational_chart')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <str-occupation-autocomplete
                                        v-model="filter.occupationId"
                                        :label="$t('module.occupation')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-data-select-autocomplete
                                        v-model="filter.workplaceId"
                                        show-default-label
                                        type="WORKER_WORKPLACE"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-data-select-autocomplete
                                        v-model="filter.companyId"
                                        show-default-label
                                        type="WORKER_COMPANY"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-data-select-autocomplete
                                        v-model="filter.stateId"
                                        show-default-label
                                        type="WORKER_STATE"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-enum-select
                                    v-model="filter.phase"
                                    :items="workerPhases"
                                    :label="$t('worker.status')"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
                <v-expansion-panel-header>
                    <strong>{{ $t('common.validation') }}</strong>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="mr-n5 ml-n5">
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12">
                                <w-enum-select
                                    v-model="filter.validationStatus"
                                    :items="validationWorkFlowStatus"
                                    :label="$t('common.state')"
                                    multiple
                                />
                            </v-col>
                            <v-col cols="12">
                                <str-step-select
                                    v-model="filter.lastState"
                                    :label="$t('common.validation')"
                                    module="TRAINING_REQUEST"
                                    by-name
                                ></str-step-select>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
        <input type="hidden"/>
    </div>
</template>